import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";
import { managementActions } from "../action-creators";
import type { HubspotExportState } from "../type";

const HUBSPOT_EXPORT_SETTINGS_STATE: HubspotExportState = {
    settings: {
        accounts: [],
        contacts: [],
    },
    columns: {
        accounts: [],
        contacts: [],
    },
    isColumnsLoading: false,
    isLoadingSettings: false,
    prevSettings: {
        accounts: [],
        contacts: [],
    },
    settingDeleteError: undefined,
    settingsCreateError: undefined,
    settingsFetchError: undefined,
    isSavingSettings: false,
    isSavedSettings: false,
    saveSettingsError: undefined,
    editableField: undefined,
    deletableField: undefined,
    exportingDomainsTo: false,
    exportingDomainsToError: undefined,
    showModalDisabledExport: false,
    notification: undefined,
    exportCompaniesStatus: false,
    loadingExportCompaniesStatus: false,
    settingExportCompaniesStatus: false,
    exportCompaniesStatusError: undefined,
};

export const hubspotExportReducer = createReducer<HubspotExportState, ActionType<typeof managementActions.hubspot>>(
    HUBSPOT_EXPORT_SETTINGS_STATE,
)
    .handleAction(managementActions.hubspot.setHsDeletableField, (state, { payload }) => ({
        ...state,
        deletableField: payload,
    }))
    .handleAction(managementActions.hubspot.fetchExportSettingsTableAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: {
            ...state.prevSettings,
            [payload.typeName]: payload.settings,
        },
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
        isLoadingSettings: false,
    }))
    .handleAction(managementActions.hubspot.fetchExportSettingsTableAsync.request, (state) => ({
        ...state,
        isLoadingSettings: true,
    }))
    .handleAction(managementActions.hubspot.fetchExportSettingsTableAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingSettings: false,
        settingsFetchError: payload,
    }))
    .handleAction(managementActions.hubspot.fetchHsExportSettingsAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: payload,
        settings: payload,
        isLoadingSettings: false,
    }))
    .handleAction(managementActions.hubspot.fetchHsExportSettingsAsync.request, (state) => ({
        ...state,
        isLoadingSettings: true,
    }))
    .handleAction(managementActions.hubspot.fetchHsExportSettingsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingSettings: false,
        settingsFetchError: payload,
    }))
    .handleAction(managementActions.hubspot.saveExportSettingsAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: { ...state.settings, [payload.typeName]: payload.settings },
        settings: { ...state.settings, [payload.typeName]: payload.settings },
        isSavingSettings: false,
        isSavedSettings: true,
    }))
    .handleAction(managementActions.hubspot.saveExportSettingsAsync.request, (state) => ({
        ...state,
        isSavingSettings: true,
    }))
    .handleAction(managementActions.hubspot.saveExportSettingsAsync.failure, (state, { payload }) => ({
        ...state,
        isSavingSettings: false,
        saveSettingsError: payload,
    }))
    .handleAction(managementActions.hubspot.exportDomainsToAsync.success, (state) => ({
        ...state,
        exportingDomainsTo: false,
    }))
    .handleAction(managementActions.hubspot.exportDomainsToAsync.request, (state) => ({
        ...state,
        exportingDomainsTo: true,
    }))
    .handleAction(managementActions.hubspot.exportDomainsToAsync.failure, (state, { payload }) => ({
        ...state,
        exportingDomainsTo: false,
        exportingDomainsToError: payload,
    }))
    .handleAction(managementActions.hubspot.showModalDisabledExport, (state, { payload }) => ({
        ...state,
        showModalDisabledExport: payload,
    }))
    .handleAction(managementActions.hubspot.setHsNotification, (state, { payload }) => ({
        ...state,
        notification: payload,
    }))
    .handleAction(managementActions.hubspot.addNewSettingsExport, (state, { payload: { typeName, settings } }) => ({
        ...state,
        settings: {
            ...state.settings,
            [typeName]: [...state.settings[typeName], ...settings],
        },
    }))
    .handleAction(managementActions.hubspot.updateHsSettingsExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
    }))
    .handleAction(managementActions.hubspot.deleteSettingExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
        deletableField: undefined,
    }))
    .handleAction(managementActions.hubspot.revertSettingsExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
    }))
    .handleAction(managementActions.hubspot.getCompaniesStatusAsync.request, (state) => ({
        ...state,
        loadingExportCompaniesStatus: true,
    }))
    .handleAction(managementActions.hubspot.getCompaniesStatusAsync.success, (state, { payload }) => ({
        ...state,
        exportCompaniesStatus: payload,
        loadingExportCompaniesStatus: false,
    }))
    .handleAction(managementActions.hubspot.getCompaniesStatusAsync.failure, (state, { payload }) => ({
        ...state,
        loadingExportCompaniesStatus: false,
        exportCompaniesStatusError: payload,
    }))
    .handleAction(managementActions.hubspot.getHsColumnsAsync.request, (state) => ({
        ...state,
        isColumnsLoading: true,
    }))
    .handleAction(managementActions.hubspot.getHsColumnsAsync.success, (state, { payload }) => ({
        ...state,
        columns: payload,
        isColumnsLoading: false,
    }))
    .handleAction(managementActions.hubspot.getHsColumnsAsync.failure, (state) => ({
        ...state,
        isColumnsLoading: false,
    }))
    .handleAction(managementActions.hubspot.saveHsAllSettingsError, (state, { payload }) => ({
        ...state,
        saveSettingsError: payload,
    }));
